import { useState, useEffect, useMemo } from "react";
import { MetricCard } from "@/components/cards/MetricCard";
import { EngagementChart } from "@/components/charts/EngagementChart";
import { BarChartCard } from "@/components/charts/BarChartCard";
import { FilterBar } from "@/components/layout/FilterBar";
import { PageHeader } from "@/components/layout/PageHeader";
import { useSharedFilters } from "@/hooks/useSharedFilters";
import { useAuth } from "@/lib/auth-context";
import { getAllowedAccounts } from "@/lib/restaurant-groups";
import { fillDateRange } from "@/lib/chart-utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ExternalLink, Eye, MessageCircle, Share2, ArrowRight, ArrowLeft, LogOut, Hash } from "lucide-react";
import { apiClient } from "@/lib/api-client";
import type { IGStory } from "@/lib/types";
import { LoadingState } from '@/components/ui/spinner';

function fmt(n: number) {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return n.toLocaleString("cs-CZ");
}

export default function IGStoriesPage() {
  const [rawData, setRawData] = useState<IGStory[]>([]);
  const [filters, setFilters] = useSharedFilters();
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    apiClient.get<IGStory[]>("/sheets/ig_stories")
      .then((data) => { setRawData(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const allData = useMemo(() => {
    if (!user) return [];
    const allowed = getAllowedAccounts(user.groupIds);
    if (user.groupIds.includes("all")) return rawData;
    return rawData.filter((s) => allowed.ig.includes(s.account_name));
  }, [rawData, user]);

  const data = useMemo(() => {
    return allData.filter((s) => {
      if (s.date < filters.dateFrom || s.date > filters.dateTo) return false;
      if (filters.accountName !== "all" && s.account_name !== filters.accountName) return false;
      return true;
    });
  }, [allData, filters]);

  const stories = data.filter((s) => s.story_id);
  const accounts = useMemo(() => [...new Set(allData.map((s) => s.account_name).filter(Boolean))].sort(), [allData]);

  const totalImpressions = stories.reduce((s, d) => s + d.story_impressions, 0);
  const totalReach = stories.reduce((s, d) => s + d.story_reach, 0);
  const totalReplies = stories.reduce((s, d) => s + d.story_replies, 0);
  const totalShares = stories.reduce((s, d) => s + d.story_shares, 0);
  const totalExits = stories.reduce((s, d) => s + d.story_exits, 0);
  const totalBack = stories.reduce((s, d) => s + d.story_taps_back, 0);
  const completionRate = totalImpressions > 0 ? 1 - (totalExits / totalImpressions) : 0;

  const reachTrend = useMemo(() => {
    const map = new Map<string, number>();
    stories.forEach((s) => map.set(s.date, (map.get(s.date) || 0) + s.story_reach));
    return fillDateRange(Array.from(map.entries()).map(([date, value]) => ({ date, value })), filters.dateFrom, filters.dateTo);
  }, [stories, filters.dateFrom, filters.dateTo]);

  const accountData = useMemo(() => {
    const map = new Map<string, { impressions: number; reach: number; exits: number }>();
    stories.forEach((s) => {
      const curr = map.get(s.account_name) || { impressions: 0, reach: 0, exits: 0 };
      curr.impressions += s.story_impressions;
      curr.reach += s.story_reach;
      curr.exits += s.story_exits;
      map.set(s.account_name, curr);
    });
    return Array.from(map.entries()).map(([name, { impressions, reach }]) => ({ name, value: impressions, value2: reach }));
  }, [stories]);

  if (loading) return <LoadingState label="Načítám data…" />;

  return (
    <div className="space-y-6">
      <PageHeader title="Instagram Stories" description="Přehled výkonu stories" />

      <FilterBar filters={filters} onChange={setFilters} accounts={accounts} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Stories" value={stories.length} icon={<Hash className="h-4 w-4 text-primary" />} />
        <MetricCard label="Dosah" value={totalReach} icon={<Eye className="h-4 w-4 text-primary" />} />
        <MetricCard label="Zobrazení" value={totalImpressions} icon={<Eye className="h-4 w-4 text-primary" />} />
        <MetricCard label="Dokončení" value={completionRate} format="percent" icon={<ArrowRight className="h-4 w-4 text-primary" />} subtitle="% kdo dokoukali" />
        <MetricCard label="Odpovědi" value={totalReplies} icon={<MessageCircle className="h-4 w-4 text-primary" />} />
        <MetricCard label="Sdílení" value={totalShares} icon={<Share2 className="h-4 w-4 text-primary" />} />
        <MetricCard label="Tap zpět" value={totalBack} icon={<ArrowLeft className="h-4 w-4 text-primary" />} subtitle="zájem o zopakování" />
        <MetricCard label="Opuštění" value={totalExits} icon={<LogOut className="h-4 w-4 text-primary" />} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <EngagementChart title="Dosah stories (trend)" data={reachTrend} color="#d4816b" label1="Dosah" />
        <BarChartCard title="Porovnání účtů" data={accountData} color="#d4816b" color2="#e8c36a" label1="Zobrazení" label2="Dosah" />
      </div>

      {/* Stories Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Všechny stories ({stories.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Datum</TableHead>
                <TableHead>Účet</TableHead>
                <TableHead className="text-right">Dosah</TableHead>
                <TableHead className="text-right">Zobrazení</TableHead>
                <TableHead className="text-right">Odpovědi</TableHead>
                <TableHead className="text-right">Sdílení</TableHead>
                <TableHead className="text-right">Tap zpět</TableHead>
                <TableHead className="text-right">Tap dál</TableHead>
                <TableHead className="text-right">Opuštění</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stories.slice(0, 50).map((s, i) => {
                const exitRate = s.story_impressions > 0 ? (s.story_exits / s.story_impressions * 100) : 0;
                return (
                  <TableRow key={s.story_id || `story-${i}`}>
                    <TableCell className="whitespace-nowrap">{s.date ? new Date(s.date).toLocaleDateString("cs-CZ") : "—"}</TableCell>
                    <TableCell>{s.account_name}</TableCell>
                    <TableCell className="text-right">{fmt(s.story_reach)}</TableCell>
                    <TableCell className="text-right">{fmt(s.story_impressions)}</TableCell>
                    <TableCell className="text-right">{fmt(s.story_replies)}</TableCell>
                    <TableCell className="text-right">{fmt(s.story_shares)}</TableCell>
                    <TableCell className="text-right">{fmt(s.story_taps_back)}</TableCell>
                    <TableCell className="text-right">{fmt(s.story_taps_forward)}</TableCell>
                    <TableCell className="text-right">
                      <span className={exitRate > 50 ? "text-red-400" : ""}>{fmt(s.story_exits)}</span>
                    </TableCell>
                    <TableCell>
                      {s.story_permalink && (
                        <a href={s.story_permalink} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-[var(--primary)]">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {stories.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                    Žádné stories v tomto období
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
